import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import DeliveryOptionsManager from "../../components/DeliveryOptionsManager";
import { toast } from "sonner";
import { Save, CreditCard, Eye, EyeOff } from "lucide-react";

export default function PaymentSettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showSk, setShowSk] = useState(false);
    const [initial, setInitial] = useState(null);
    const [pk, setPk] = useState("");
    const [sk, setSk] = useState("");
    const [onlineEnabled, setOnlineEnabled] = useState(true);
    const [payLater, setPayLater] = useState(true);
    const [fpxEnabled, setFpxEnabled] = useState(true);
    const [deliveryOptions, setDeliveryOptions] = useState([]);
    const [source, setSource] = useState("none");
    const [secretKeyMode, setSecretKeyMode] = useState("none");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/payment-settings");
            setPk(data.publishable_key || "");
            setSk(data.secret_key_masked || "");
            setOnlineEnabled(!!data.online_payment_enabled);
            setPayLater(!!data.allow_pay_later);
            setFpxEnabled(data.fpx_payment_enabled !== false);
            setDeliveryOptions(data.delivery_options || []);
            setSource(data.secret_key_source || "none");
            setSecretKeyMode(data.secret_key_mode || "none");
            setInitial({
                pk: data.publishable_key || "",
                sk: data.secret_key_masked || "",
                onlineEnabled: !!data.online_payment_enabled,
                payLater: !!data.allow_pay_later,
                fpxEnabled: data.fpx_payment_enabled !== false,
                deliveryOptions: data.delivery_options || [],
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "读取失败");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const dirty = initial && (
        pk !== initial.pk ||
        (sk && !sk.includes("····")) ||
        onlineEnabled !== initial.onlineEnabled ||
        payLater !== initial.payLater ||
        fpxEnabled !== initial.fpxEnabled ||
        JSON.stringify(deliveryOptions) !== JSON.stringify(initial.deliveryOptions)
    );

    const mode = (() => {
        const typedKey = sk && !sk.includes("····") ? sk : "";
        const activeMode = typedKey.startsWith("sk_live_")
            ? "live"
            : typedKey.startsWith("sk_test_")
                ? "test"
                : secretKeyMode;
        if (activeMode === "live") {
            return { label: "正式模式 · Live", cls: "bg-red-100 text-red-700 border-red-200" };
        }
        if (activeMode === "test") {
            return { label: "测试模式 · Test", cls: "bg-amber-100 text-amber-700 border-amber-200" };
        }
        return { label: "未配置", cls: "bg-slate-100 text-slate-500 border-slate-200" };
    })();

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                publishable_key: pk,
                online_payment_enabled: onlineEnabled,
                allow_pay_later: payLater,
                fpx_payment_enabled: fpxEnabled,
                delivery_options: deliveryOptions,
            };
            if (sk && !sk.includes("····")) body.secret_key = sk;
            const { data } = await api.put("/admin/payment-settings", body);
            setPk(data.publishable_key || "");
            setSk(data.secret_key_masked || "");
            setOnlineEnabled(!!data.online_payment_enabled);
            setPayLater(!!data.allow_pay_later);
            setFpxEnabled(data.fpx_payment_enabled !== false);
            setDeliveryOptions(data.delivery_options || []);
            setSource(data.secret_key_source || "none");
            setSecretKeyMode(data.secret_key_mode || "none");
            setInitial({
                pk: data.publishable_key || "",
                sk: data.secret_key_masked || "",
                onlineEnabled: !!data.online_payment_enabled,
                payLater: !!data.allow_pay_later,
                fpxEnabled: data.fpx_payment_enabled !== false,
                deliveryOptions: data.delivery_options || [],
            });
            toast.success("收款设置已保存");
        } catch (e) {
            toast.error(e.response?.data?.detail || "保存失败");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="payment-settings-page">
            <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">系统设置</div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1 flex items-center gap-3">
                    <CreditCard className="h-7 w-7 text-gold-600" /> Stripe 收款
                </h1>
                <p className="text-sm text-gold-500 mt-1">
                    密钥保存在数据库；密文只回传掩码
                </p>
            </div>

            <Card className="p-6 border-slate-200 shadow-sm max-w-3xl" data-testid="payment-settings-card">
                <div className="flex items-center gap-2 mb-6">
                    <Badge
                        data-testid="payment-mode-badge"
                        className={`border ${mode.cls} hover:${mode.cls}`}
                    >
                        {mode.label}
                    </Badge>
                    <span className="text-xs text-gold-500">当前密钥来源：<span className="font-mono">{source}</span></span>
                </div>

                {loading ? (
                    <div className="text-sm text-gold-500">加载中...</div>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Publishable Key（可公开）</Label>
                            <Input
                                data-testid="payment-pk-input"
                                value={pk}
                                onChange={(e) => setPk(e.target.value)}
                                placeholder="pk_test_... 或 pk_live_..."
                                className="font-mono text-xs"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Secret Key（服务器密钥）</Label>
                            <div className="relative">
                                <Input
                                    data-testid="payment-sk-input"
                                    type={showSk ? "text" : "password"}
                                    value={sk}
                                    onChange={(e) => setSk(e.target.value)}
                                    placeholder="sk_test_... 或 sk_live_..."
                                    className="font-mono text-xs pr-10"
                                />
                                <button
                                    type="button"
                                    data-testid="payment-secret-visibility-button"
                                    onClick={() => setShowSk(!showSk)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                                    title={showSk ? "隐藏" : "显示"}
                                >
                                    {showSk ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <div className="text-xs text-gold-500">
                                当前显示的是掩码，粘贴新值才会覆盖，留空则保留旧值。
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 pt-2 md:grid-cols-2">
                            <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50">
                                <div>
                                    <div className="text-sm font-medium text-slate-900">启用在线付款</div>
                                    <div className="text-xs text-gold-500 mt-0.5">关闭后 checkout 接口会返回 400</div>
                                </div>
                                <Switch data-testid="payment-online-switch" checked={onlineEnabled} onCheckedChange={setOnlineEnabled} />
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50">
                                <div>
                                    <div className="text-sm font-medium text-slate-900">允许稍后付款</div>
                                    <div className="text-xs text-gold-500 mt-0.5">客户可下单后由客服联系收款</div>
                                </div>
                                <Switch data-testid="payment-paylater-switch" checked={payLater} onCheckedChange={setPayLater} />
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-gold-50 md:col-span-2">
                                <div>
                                    <div className="text-sm font-medium text-slate-900">启用 FPX 网上银行</div>
                                    <div className="text-xs text-gold-500 mt-0.5">
                                        仅适用于 MYR；顾客会在 Stripe 结账页选择银行并完成验证。
                                    </div>
                                </div>
                                <Switch
                                    data-testid="payment-fpx-switch"
                                    checked={fpxEnabled}
                                    onCheckedChange={setFpxEnabled}
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                            <Button
                                data-testid="save-payment-btn"
                                onClick={save}
                                disabled={saving || !dirty}
                                className="bg-gold-600 hover:bg-gold-700 text-white gap-2"
                            >
                                <Save className="h-4 w-4" /> {saving ? "保存中..." : "保存"}
                            </Button>
                            {dirty && <span className="text-xs text-amber-600">有未保存的修改</span>}
                        </div>
                    </div>
                )}
            </Card>
            <DeliveryOptionsManager options={deliveryOptions} onChange={setDeliveryOptions} />
            <div className="flex justify-end">
                <Button
                    type="button"
                    data-testid="save-delivery-options-button"
                    onClick={save}
                    disabled={saving || !dirty}
                    className="gap-2 bg-gold-600 text-white hover:bg-gold-700"
                >
                    <Save className="h-4 w-4" />
                    {saving ? "保存中..." : "保存配送方式"}
                </Button>
            </div>
        </div>
    );
}
